import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme, Dialog, IconButton, Alert } from '@mui/material';
import { useGetOne, useTranslate } from 'react-admin';
import ClosePopupIcon from '@/assets/icons/close-popup-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { IMAGE_DIMENSIONS } from '@/constants/ActivitiesConfig.js';
import { useFullImageLoader } from '@/composables/useFullImageLoader.js';
import { useSwipeNavigation } from '@/composables/useSwipeNavigation.js';
import ImageNavigationButtons from './ImageNavigationButtons.jsx';

const ImageDisplay = React.memo(({ blobUrl, sx, onClick, hasError, imageIndex }) => {
    if (!blobUrl) {
        if (hasError) {
            return (
                <Box
                    sx={{
                        ...sx,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: Palette.gray.gray100,
                        gap: 2,
                        padding: 2,
                    }}
                >
                    <Alert severity='error' sx={{ width: '100%' }}>
                        Failed to load image
                    </Alert>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    ...sx,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: Palette.gray.gray100,
                }}
            >
                <CircularProgress size={20} />
            </Box>
        );
    }

    return (
        <Box
            component='img'
            src={blobUrl}
            alt={`Image ${imageIndex + 1}`}
            onClick={onClick}
            sx={{
                ...sx,
                objectFit: 'contain',
                width: '100%',
                height: 'auto',
                maxHeight: '100%',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: '32px',
            }}
        />
    );
});

ImageDisplay.propTypes = {
    blobUrl: PropTypes.string,
    sx: PropTypes.object,
    onClick: PropTypes.func,
    hasError: PropTypes.bool,
    imageIndex: PropTypes.number,
};

ImageDisplay.displayName = 'ImageDisplay';

const resolveIsServiceInspection = (resource, serviceInspectionId, serviceRequestId) => {
    if (resource) return resource === 'service-inspection';
    if (serviceInspectionId && !serviceRequestId) return true;
    if (serviceRequestId && !serviceInspectionId) return false;
    return globalThis.location.pathname.includes('/service-inspection');
};

const CommentImageSlider = ({ open, onClose, images, initialIndex, serviceRequestId, serviceInspectionId, blobUrls = {}, resource, onLoadingChange }) => {
    const isServiceInspection = resolveIsServiceInspection(resource, serviceInspectionId, serviceRequestId);
    const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data: requestData } = useGetOne(
        resource,
        { id: isServiceInspection ? serviceInspectionId : serviceRequestId },
        { enabled: !!(isServiceInspection ? serviceInspectionId : serviceRequestId) && open },
    );
    const translate = useTranslate();

    const { fullImageBlobUrls, imageErrors, loadingIndexes, loadFullImageForIndex } = useFullImageLoader({
        images,
        currentIndex,
        serviceRequestId,
        serviceInspectionId,
        enabled: open && !!images?.length,
        resource,
        blobUrls,
    });

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex || 0);
        }
    }, [initialIndex, open]);

    const handleNext = useCallback(async () => {
        const newIndex = Math.min(currentIndex + 1, images.length - 1);
        if (newIndex !== currentIndex && images[newIndex]) {
            setCurrentIndex(newIndex);
            await loadFullImageForIndex(newIndex);
        }
    }, [currentIndex, images, loadFullImageForIndex]);

    const handlePrev = useCallback(async () => {
        const newIndex = Math.max(currentIndex - 1, 0);
        if (newIndex !== currentIndex && images[newIndex]) {
            setCurrentIndex(newIndex);
            await loadFullImageForIndex(newIndex);
        }
    }, [currentIndex, images, loadFullImageForIndex]);

    const handleKeyDown = useCallback(
        e => {
            if (!open) return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    handlePrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    handleNext();
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
                default:
                    break;
            }
        },
        [open, handlePrev, handleNext, onClose],
    );

    const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeNavigation({
        onNext: handleNext,
        onPrev: handlePrev,
        currentIndex,
        totalImages: images?.length || 0,
        enabled: isMobile && open,
    });

    useEffect(() => {
        if (open) {
            globalThis.addEventListener('keydown', handleKeyDown);
            return () => {
                globalThis.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [open, handleKeyDown]);

    const isLoading = useMemo(() => open && Object.keys(loadingIndexes).length > 0, [open, loadingIndexes]);

    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(isLoading);
        }
    }, [isLoading, onLoadingChange]);

    const isFirst = useMemo(() => currentIndex === 0, [currentIndex]);
    const isLast = useMemo(() => currentIndex === (images?.length || 0) - 1, [currentIndex, images?.length]);
    const prevIndex = useMemo(() => currentIndex - 1, [currentIndex]);
    const nextIndex = useMemo(() => currentIndex + 1, [currentIndex]);

    const currentBlobUrl = fullImageBlobUrls[currentIndex] || blobUrls[currentIndex];
    const prevBlobUrl = isFirst ? null : fullImageBlobUrls[prevIndex] || blobUrls[prevIndex];
    const nextBlobUrl = isLast ? null : fullImageBlobUrls[nextIndex] || blobUrls[nextIndex];

    const formattedId = useMemo(() => {
        return requestData?.customId ? `${requestData.customId.toString().padStart(6, '0')}` : '';
    }, [requestData?.customId]);

    if (!open || !images || images.length === 0) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            aria-labelledby='image-slider-title'
            aria-describedby='image-slider-description'
            PaperProps={{
                sx: {
                    width: '100vw',
                    height: '100vh',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    margin: 0,
                    borderRadius: 0,
                    backgroundColor: Palette.white,
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: Palette.white,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '32px 56px',
                    }}
                >
                    <Typography id='image-slider-title' variant='h4' sx={{ fontWeight: 600 }}>
                        {translate('resources.serviceRequest.activityList.imagesForRequest', { id: formattedId })}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        aria-label='Close image slider'
                        sx={{
                            color: Palette.black,
                            width: 52,
                            height: 44,
                            '&:hover': {
                                backgroundColor: Palette.gray.gray100,
                            },
                        }}
                    >
                        <ClosePopupIcon />
                    </IconButton>
                </Box>

                {/* Images Container */}
                <Box
                    id='image-slider-description'
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        padding: '24px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Previous Image */}
                    <Box
                        sx={{
                            width: isMobile ? '100%' : `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            maxWidth: `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            height: '100%',
                            flexShrink: 0,
                            opacity: 0.4,
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {!isFirst && (
                            <ImageDisplay
                                blobUrl={prevBlobUrl}
                                hasError={imageErrors[prevIndex]}
                                imageIndex={prevIndex}
                                sx={{
                                    cursor: 'pointer',
                                }}
                                onClick={handlePrev}
                            />
                        )}
                    </Box>

                    {/* Current Image */}
                    <Box
                        sx={{
                            width: isMobile ? '100%' : `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            maxWidth: `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            height: '100%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ImageDisplay blobUrl={currentBlobUrl} hasError={imageErrors[currentIndex]} imageIndex={currentIndex} />
                    </Box>

                    {/* Next Image */}
                    <Box
                        sx={{
                            width: isMobile ? '100%' : `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            maxWidth: `${IMAGE_DIMENSIONS.SLIDER_WIDTH_DESKTOP}px`,
                            height: '100%',
                            flexShrink: 0,
                            opacity: 0.4,
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {!isLast && (
                            <ImageDisplay
                                blobUrl={nextBlobUrl}
                                hasError={imageErrors[nextIndex]}
                                imageIndex={nextIndex}
                                sx={{
                                    cursor: 'pointer',
                                }}
                                onClick={handleNext}
                            />
                        )}
                    </Box>
                </Box>
                <ImageNavigationButtons onPrev={handlePrev} onNext={handleNext} isFirst={isFirst} isLast={isLast} />
            </Box>
        </Dialog>
    );
};

CommentImageSlider.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            changeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            imageName: PropTypes.string.isRequired,
        }),
    ).isRequired,
    initialIndex: PropTypes.number,
    serviceRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    serviceInspectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    blobUrls: PropTypes.objectOf(PropTypes.string),
    resource: PropTypes.string,
    onLoadingChange: PropTypes.func,
};

export default CommentImageSlider;
